<template>
  <Form @submit="handleSubmit" @invalid-submit="onInvalidSubmit">
    <BaseContainer>
      <CheckoutLayoutHeader
        :title="props.title"
        :has-discount-code="props.hasDiscountCode"
        :has-gift-card="props.hasGiftCard"
        :onCodeChange="handleCodeChange"
        :code-input="codeInput"
        @update:countdown-fixed="(val) => countdownFixed = val"
      >
        <slot name="header" />
      </CheckoutLayoutHeader>

      <div class="flex flex-col md:flex-row gap-10 justify-between" v-if="router.currentRoute.value.name != 'checkout-payment'">
        <div class="flex-1 md:max-w-2xl">
          <slot />
        </div>

        <div class="w-full md:w-120 md:max-w-1/2" v-if="cartStore.items.length > 0">
          <CheckoutCodeInput
            v-if="hasDiscountCode || hasGiftCard"
            ref="codeInput"
            :class="hasDiscountCode ? 'md:block hidden' : ''"
            @on-change="handleCodeChange"
            :hasGiftCard="hasGiftCard"
            :hasDiscountCode="hasDiscountCode && checkoutStore.event.has_vouchercodes == 1"
          />

          <div class="md:sticky md:top-24">
            <div
              v-if="countdownFixed && activeExpire > 0 && route.name != 'checkout-payment'"
              class="hidden md:flex items-center gap-1 font-semibold text-sm justify-end mb-2"
            >
              <StopwatchIcon class="fill-primary w-6 h-6" />
              <BaseCountdown :date="activeExpire" /> {{ $t('checkout.countdown') }}
            </div>
            <CheckoutSummary ref="summary" />
          </div>
        </div>
      </div>
      <div class="flex-1 md:max-w-2xl" v-if="router.currentRoute.value.name == 'checkout-payment'">
        <CheckoutSummary ref="summary" />
      </div>
    </BaseContainer>

    <BaseModal v-model="checkoutStore.paymentModal" size="md">
      <template #header>{{ $t('checkout.choose_payment_method') }}</template>

      <div class="p-6 space-y-4">
        <CheckoutPaymentLink
          paymentMethod="dankort"
          :price="cartStore.cartTotals.total - (checkoutStore.startPurchaseData.dankort_discount || 0)"
          title="Dankort"
          :discount="checkoutStore.startPurchaseData.dankort_discount"
        >
          <img
            src="/images/dankort.png"
            class="h-10 w-15 object-contain ml-auto"
            alt="Dankort"
          />
        </CheckoutPaymentLink>

        <CheckoutPaymentLink
          title="Mastercard / Visa / MobilPay"
          paymentMethod="creditcard"
          :price="cartStore.cartTotals.total"
          v-if="cartStore.cardTypes.includes('mastercard') || cartStore.cardTypes.includes('visa')"
        >
          <div class="flex ml-auto">
            <img
              src="/images/mastercard.png"
              class="h-10 w-15 object-contain"
              alt="Mastercard"
              v-if="cartStore.cardTypes.includes('mastercard')"
              @click="websocket.doPlaceOrder('creditcard')"
            />
            <img
              src="/images/visa.png"
              class="h-10 w-15 object-contain"
              alt="Visa"
              v-if="cartStore.cardTypes.includes('visa')"
              @click="websocket.doPlaceOrder('creditcard')"
            />
          </div>
        </CheckoutPaymentLink>
      </div>
    </BaseModal>

    <CheckoutExitModal />
  </Form>
</template>

<script setup lang="ts">
  import {
    Form,
    type InvalidSubmissionHandler,
    type SubmissionHandler
  } from 'vee-validate';
  import { computed, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import type { CodeItem } from '@/types';
  import { setNonRequiredFields } from '@/utils/setNonRequiredFields';
  import { useWebsocketStore } from '@/websocket/websocket';

  import StopwatchIcon from '../icons/StopwatchIcon.vue';
  import BaseContainer from '../ui/BaseContainer.vue';
  import BaseCountdown from '../ui/BaseCountdown.vue';
  import BaseModal from '../ui/BaseModal.vue';
  import CheckoutCodeInput from './CheckoutCodeInput.vue';
  import CheckoutExitModal from './CheckoutExitModal.vue';
  import CheckoutLayoutHeader from './CheckoutLayoutHeader.vue';
  import CheckoutPaymentLink from './CheckoutPaymentLink.vue';
  import CheckoutSummary from './CheckoutSummary.vue';

  const router = useRouter();
  const route = useRoute();

  const props = defineProps<{
    title: string;
    nextLink?: string;
    hasDiscountCode?: boolean;
    hasGiftCard?: boolean;
  }>();

  const cartStore = useCartStore();
  const checkoutStore = useCheckoutStore();
  const websocket = useWebsocketStore();

  const countdownFixed = ref(false);
  const activeExpire = computed(() =>
    route.name === 'checkout-payment' ? cartStore.paymentFailedExpire : cartStore.cartExpire
  );

  const codeInput = ref<InstanceType<typeof CheckoutCodeInput> | null>(null);

  const onInvalidSubmit: InvalidSubmissionHandler = submission => {
    const firstErrorFieldName = Object.keys(submission.errors)[0];
    const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
    if (el) {
      el.scrollIntoView();
    }
    console.log('Invalid submission:', submission);
  };

  /*onMounted(() => {
    const script = document.createElement('script');
    script.src = 'https://ssl.ditonlinebetalingssystem.dk/integration/ewindow/paymentwindow.js';
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    document.head.appendChild(script);
  });*/

  const handleSubmit: SubmissionHandler = (values, ctx) => {
    const route = router.currentRoute.value.name;
      console.log('Submitting data:', values);

    if (route == 'checkout-information') {

      setNonRequiredFields();

      //checkout.paymentModal = true;
      checkoutStore.setOrderData(cartStore.currentOrderData);
      websocket.send('orderdata', cartStore.currentOrderData, undefined);
    } 
    else if (route == 'checkout-tickets' || route == 'checkout-addons') {
      /*console.log('Submitting ticket/addon data:', values);
      console.log('Submitting ticket/addon data:', ctx);

      console.log('Submitting data:', data); */

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((ctx as any).evt.submitter?._value == 'continue') {
        router.push('/?prevent-guard=true');
      } 
      else {
        router.push(props.nextLink || '/');
      }
    }
  };

  const summary = ref<InstanceType<typeof CheckoutSummary> | null>(null);

  const emit = defineEmits(['onCodeChange']);

  function handleCodeChange(codes: CodeItem[]) {
    emit('onCodeChange', codes);

    if (summary.value) {
      summary.value.calculateSummary();
    }
  }
</script>
